import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Hook pour gérer la déconnexion automatique après inactivité
 * @param {number} inactivityTime - Temps d'inactivité en millisecondes (défaut: 30 minutes)
 * @param {number} warningTime - Temps avant alerte en millisecondes (défaut: 5 minutes avant expiration)
 */
export const useInactivityLogout = (
  inactivityTime = 30 * 60 * 1000, // 30 minutes
  warningTime = 5 * 60 * 1000 // 5 minutes avant expiration
) => {
  const { currentUser, logout } = useAuth();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Events qui comptent comme activité
  const activityEvents = [
    'mousedown',
    'mousemove', 
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ];

  const showWarning = () => {
    console.log('⚠️ Avertissement: Déconnexion automatique dans 5 minutes');
    
    // Créer une notification native si possible
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Session expirée bientôt', {
        body: 'Votre session expirera dans 5 minutes par inactivité',
        icon: '/favicon.ico',
        tag: 'session-warning'
      });
    }

    // Dispatcher un événement custom pour l'affichage UI
    window.dispatchEvent(new CustomEvent('sessionWarning', {
      detail: { timeRemaining: warningTime }
    }));
  };

  const resetTimer = () => {
    if (!currentUser) return;

    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    lastActivityRef.current = Date.now();

    // Set warning timer (5 minutes before logout)
    warningTimeoutRef.current = setTimeout(() => {
      showWarning();
    }, inactivityTime - warningTime);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      console.log('🔒 Déconnexion automatique par inactivité');
      
      // Dispatcher un événement avant déconnexion
      window.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: { reason: 'inactivity' }
      }));
      
      logout();
    }, inactivityTime);
  };

  const handleActivity = () => {
    resetTimer();
  };

  useEffect(() => {
    if (!currentUser) {
      // Clear timers if user logs out manually
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      return;
    }

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Add event listeners for activity detection
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [currentUser, inactivityTime, warningTime]);

  // Extension manuelle de session
  const extendSession = () => {
    console.log('🔄 Extension manuelle de session');
    resetTimer();
    
    // Fermer l'avertissement s'il était affiché
    window.dispatchEvent(new CustomEvent('sessionWarningDismissed'));
  };

  return {
    extendSession,
    lastActivity: lastActivityRef.current,
    timeRemaining: () => {
      const elapsed = Date.now() - lastActivityRef.current;
      return Math.max(0, inactivityTime - elapsed);
    }
  };
};